/**
 * App root — composes the providers (react-query) and the router.
 *
 * Routes:
 *   /login             → login page (no guard, no AppShell)
 *   /                  → redirects to /hr (the only Phase 1 page wired up)
 *   /hr                → HR & People (Section 3) — Tabs 1-4 functional
 *   /stub/:slug        → placeholder for every other sidebar nav item
 *
 * Every protected route renders inside the AppShell so the sidebar is
 * always present, and every protected route is wrapped in <RequireAuth>
 * which redirects unauthenticated users to /login.
 *
 * The `<Routes>` block is factored into `<AppRoutes />` so tests can
 * render it inside a `MemoryRouter` without re-instantiating the provider
 * stack. The top-level `<App />` wires the real `<BrowserRouter>` +
 * `<QueryClientProvider>` around it.
 */

import {
  Navigate,
  Route,
  BrowserRouter,
  Routes,
  useLocation,
} from 'react-router';
import { QueryClientProvider } from '@tanstack/react-query';
// Above every lazy page: the dev server turns this import into a variable at this line.
import { lazy, Suspense, useState } from 'react';
import { BranchProvider } from './lib/branchContext';
import { queryClient } from './lib/queryClient';
import { MutationErrorToast } from './components/MutationErrorToast';
import { DocumentViewer } from './components/DocumentViewer';
import { getAccessToken } from './lib/auth';
import { landingRoute } from './lib/landingRoute';
import { getDriverTabletToken } from './lib/driverTabletDeviceAuth';
import { getKioskToken } from './lib/kioskApi';
import { AppShell } from './layout/AppShell';
import { DevolvedRoute } from './components/DevolvedRoute';
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const DirectoryPage = lazy(() => import('./pages/directory/DirectoryPage').then((m) => ({ default: m.DirectoryPage })));
const GlossaryPage = lazy(() => import('./pages/glossary/GlossaryPage').then((m) => ({ default: m.GlossaryPage })));
const CompanyCalendarPage = lazy(() => import('./pages/calendar/CompanyCalendarPage').then((m) => ({ default: m.CompanyCalendarPage })));
const ContractsPage = lazy(() => import('./pages/contracts/ContractsPage').then((m) => ({ default: m.ContractsPage })));
const ContractsSignPage = lazy(() => import('./pages/contracts/ContractsSignPage').then((m) => ({ default: m.ContractsSignPage })));
const InformationSecurityPage = lazy(() => import('./pages/informationSecurity/InformationSecurityPage').then((m) => ({ default: m.InformationSecurityPage })));
const InformationSecurityIncidentPage = lazy(() => import('./pages/informationSecurity/InformationSecurityIncidentPage').then((m) => ({ default: m.InformationSecurityIncidentPage })));
const AccessReviewPage = lazy(() => import('./pages/informationSecurity/AccessReviewPage').then((m) => ({ default: m.AccessReviewPage })));
const KioskPage = lazy(() => import('./pages/kiosk/KioskPage').then((m) => ({ default: m.KioskPage })));
const KioskProvisionPage = lazy(() => import('./pages/kiosk/KioskProvisionPage').then((m) => ({ default: m.KioskProvisionPage })));
const DriverTabletPage = lazy(() => import('./pages/driverTablet/DriverTabletPage').then((m) => ({ default: m.DriverTabletPage })));
const AuditorPortalPage = lazy(() => import('./pages/auditorPortal/AuditorPortalPage').then((m) => ({ default: m.AuditorPortalPage })));
const CareHomePortalPage = lazy(() => import('./pages/careHomePortal/CareHomePortalPage').then((m) => ({ default: m.CareHomePortalPage })));
const ContractSignPage = lazy(() => import('./pages/contractSign/ContractSignPage').then((m) => ({ default: m.ContractSignPage })));
const SatisfactionSurveyPage = lazy(() => import('./pages/satisfactionSurvey/SatisfactionSurveyPage').then((m) => ({ default: m.SatisfactionSurveyPage })));
const SupplierQuestionnairePage = lazy(() => import('./pages/supplierQuestionnaire/SupplierQuestionnairePage').then((m) => ({ default: m.SupplierQuestionnairePage })));
const ReadSignPage = lazy(() => import('./pages/readSign/ReadSignPage').then((m) => ({ default: m.ReadSignPage })));
const AuditAccessPage = lazy(() => import('./pages/auditAccess/AuditAccessPage').then((m) => ({ default: m.AuditAccessPage })));
const DossetteReturnPage = lazy(() => import('./pages/dossetteBilling/DossetteReturnPage').then((m) => ({ default: m.DossetteReturnPage })));
const DriverTabletProvisionPage = lazy(() => import('./pages/driverTablet/DriverTabletProvisionPage').then((m) => ({ default: m.DriverTabletProvisionPage })));
const KioskSetupPage = lazy(() => import('./pages/admin/KioskSetupPage').then((m) => ({ default: m.KioskSetupPage })));
const BackupKioskPage = lazy(() => import('./pages/kiosk/BackupKioskPage').then((m) => ({ default: m.BackupKioskPage })));
// 2026-06-05 (FT-DTAB-A14 phase 2) — TM admin: Weekly Rest Compensation ledger.
const WeeklyRestCompensationPage = lazy(() => import('./pages/admin/WeeklyRestCompensationPage').then((m) => ({ default: m.WeeklyRestCompensationPage })));
// 2026-06-05 — TM admin: Walkaround Checks (DVSA evidence).
const WalkaroundChecksPage = lazy(() => import('./pages/admin/WalkaroundChecksPage').then((m) => ({ default: m.WalkaroundChecksPage })));
const AdministrationPage = lazy(() => import('./pages/administration/AdministrationPage').then((m) => ({ default: m.AdministrationPage })));
const PayRateStructuresAdminPage = lazy(() => import('./pages/administration/PayRateStructuresAdminPage').then((m) => ({ default: m.PayRateStructuresAdminPage })));
const TimeZoneAdminPage = lazy(() => import('./pages/administration/TimeZoneAdminPage').then((m) => ({ default: m.TimeZoneAdminPage })));
const InvoiceBrandingPage = lazy(() => import('./pages/administration/InvoiceBrandingPage').then((m) => ({ default: m.InvoiceBrandingPage })));
// 2026-06-10 — Setup Journey is a full page (was an overlay on the Admin hub).
const SetupJourneyPage = lazy(() => import('./pages/administration/SetupJourneyPage').then((m) => ({ default: m.SetupJourneyPage })));
import { useQuery } from '@tanstack/react-query';
const AgreementPage = lazy(() => import('./pages/agreement/AgreementPage').then((m) => ({ default: m.AgreementPage })));
import { fetchAgreementToSign } from './lib/tenantApi';
const EmployeeAppPage = lazy(() => import('./pages/admin/EmployeeAppPage').then((m) => ({ default: m.EmployeeAppPage })));
const DocumentRegisterPage = lazy(() => import('./pages/admin/DocumentRegisterPage').then((m) => ({ default: m.DocumentRegisterPage })));
const DocumentDetailPage = lazy(() => import('./pages/admin/DocumentDetailPage').then((m) => ({ default: m.DocumentDetailPage })));
const RolesPage = lazy(() => import('./pages/admin/RolesPage').then((m) => ({ default: m.RolesPage })));
const DepartmentsPage = lazy(() => import('./pages/admin/DepartmentsPage').then((m) => ({ default: m.DepartmentsPage })));
const OrgChartPage = lazy(() => import('./pages/admin/OrgChartPage').then((m) => ({ default: m.OrgChartPage })));
const SubscriptionPage = lazy(() => import('./pages/admin/SubscriptionPage').then((m) => ({ default: m.SubscriptionPage })));
const DirectDebitReturnPage = lazy(() => import('./pages/admin/DirectDebitReturnPage').then((m) => ({ default: m.DirectDebitReturnPage })));
const DirectDebitLinkPage = lazy(() => import('./pages/subscriptionBilling/DirectDebitLinkPage').then((m) => ({ default: m.DirectDebitLinkPage })));
const DirectDebitLinkReturnPage = lazy(() => import('./pages/subscriptionBilling/DirectDebitLinkReturnPage').then((m) => ({ default: m.DirectDebitLinkReturnPage })));
const GuidancePage = lazy(() => import('./pages/guidance/GuidancePage').then((m) => ({ default: m.GuidancePage })));
const HowToPage = lazy(() => import('./pages/howTo/HowToPage').then((m) => ({ default: m.HowToPage })));
const HrPage = lazy(() => import('./pages/hr/HrPage').then((m) => ({ default: m.HrPage })));
const FireRegisterPrintView = lazy(() => import('./pages/hr/FireRegisterPrintView').then((m) => ({ default: m.FireRegisterPrintView })));
// JN-ADMIN-001 — JeanieIQ Platform Admin (separate auth from tenant).
const PlatformAdminLoginPage = lazy(() => import('./pages/platformAdmin/PlatformAdminLoginPage').then((m) => ({ default: m.PlatformAdminLoginPage })));
const AdminForgotPasswordPage = lazy(() => import('./pages/platformAdmin/AdminForgotPasswordPage').then((m) => ({ default: m.AdminForgotPasswordPage })));
const AdminResetPasswordPage = lazy(() => import('./pages/platformAdmin/AdminResetPasswordPage').then((m) => ({ default: m.AdminResetPasswordPage })));
const PlatformAdminHomePage = lazy(() => import('./pages/platformAdmin/PlatformAdminHomePage').then((m) => ({ default: m.PlatformAdminHomePage })));
const PlatformAdminTenantsPage = lazy(() => import('./pages/platformAdmin/PlatformAdminTenantsPage').then((m) => ({ default: m.PlatformAdminTenantsPage })));
const PlatformAdminTenantDetailPage = lazy(() => import('./pages/platformAdmin/PlatformAdminTenantDetailPage').then((m) => ({ default: m.PlatformAdminTenantDetailPage })));
// JN-PLATFORM-SETTINGS-001 — operator-tweakable platform settings.
const PlatformAdminSettingsPage = lazy(() => import('./pages/platformAdmin/PlatformAdminSettingsPage').then((m) => ({ default: m.PlatformAdminSettingsPage })));
// 2026-06-28 — Platform Admin → Country Configuration (Shane + Matt).
const CountryConfigPage = lazy(() => import('./pages/platformAdmin/CountryConfigPage').then((m) => ({ default: m.CountryConfigPage })));
const WorkingCountryPage = lazy(() => import('./pages/platformAdmin/WorkingCountryPage').then((m) => ({ default: m.WorkingCountryPage })));
// 2026-06-28 — JeanieIQ-admin pages moved off the Tenants page (Shane + Matt).
const FailedLoginsPage = lazy(() => import('./pages/platformAdmin/FailedLoginsPage').then((m) => ({ default: m.FailedLoginsPage })));
const ErrorLogPage = lazy(() => import('./pages/platformAdmin/ErrorLogPage').then((m) => ({ default: m.ErrorLogPage })));
const EarlyAccessPage = lazy(() => import('./pages/platformAdmin/EarlyAccessPage').then((m) => ({ default: m.EarlyAccessPage })));
const SignupRequestsPage = lazy(() => import('./pages/platformAdmin/SignupRequestsPage').then((m) => ({ default: m.SignupRequestsPage })));
const LocumsPage = lazy(() => import('./pages/platformAdmin/LocumsPage').then((m) => ({ default: m.LocumsPage })));
const SubscriptionBillingPage = lazy(() => import('./pages/platformAdmin/SubscriptionBillingPage').then((m) => ({ default: m.SubscriptionBillingPage })));
const PlatformAdminPricingPage = lazy(() => import('./pages/platformAdmin/PlatformAdminPricingPage').then((m) => ({ default: m.PlatformAdminPricingPage })));
const AdminsPage = lazy(() => import('./pages/platformAdmin/AdminsPage').then((m) => ({ default: m.AdminsPage })));
// TODO 530 - a Platform Admin page your permission level does not open.
const AdminSectionGate = lazy(() => import('./pages/platformAdmin/AdminSectionGate').then((m) => ({ default: m.AdminSectionGate })));
const AuditLogPage = lazy(() => import('./pages/platformAdmin/AuditLogPage').then((m) => ({ default: m.AuditLogPage })));
const ImpersonationLandingPage = lazy(() => import('./pages/platformAdmin/ImpersonationLandingPage').then((m) => ({ default: m.ImpersonationLandingPage })));
const MyProfilePage = lazy(() => import('./pages/myProfile/MyProfilePage').then((m) => ({ default: m.MyProfilePage })));
const OnboardingRegisterPage = lazy(() => import('./pages/onboarding/OnboardingRegisterPage').then((m) => ({ default: m.OnboardingRegisterPage })));
const SignupPage = lazy(() => import('./pages/signup/SignupPage').then((m) => ({ default: m.SignupPage })));
const LegalDocumentPage = lazy(() => import('./pages/legal/LegalDocumentPage').then((m) => ({ default: m.LegalDocumentPage })));
const SignupConfirmPage = lazy(() => import('./pages/signup/SignupConfirmPage').then((m) => ({ default: m.SignupConfirmPage })));
const LoginPage = lazy(() => import('./pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })));
const PlatformPage = lazy(() => import('./pages/PlatformPage').then((m) => ({ default: m.PlatformPage })));
const PayrollPage = lazy(() => import('./pages/payroll/PayrollPage').then((m) => ({ default: m.PayrollPage })));
const MotorTradePage = lazy(() => import('./pages/motorTrade/MotorTradePage').then((m) => ({ default: m.MotorTradePage })));
const SubContractPage = lazy(() => import('./pages/subContract/SubContractPage').then((m) => ({ default: m.SubContractPage })));
const CashflowPage = lazy(() => import('./pages/cashflow/CashflowPage').then((m) => ({ default: m.CashflowPage })));
const TroncPage = lazy(() => import('./pages/tronc/TroncPage').then((m) => ({ default: m.TroncPage })));
const HolidayPage = lazy(() => import('./pages/holiday/HolidayPage').then((m) => ({ default: m.HolidayPage })));
const AppraisalsDisciplinePage = lazy(() => import('./pages/appraisalsDiscipline/AppraisalsDisciplinePage').then((m) => ({ default: m.AppraisalsDisciplinePage })));
const NearMissPage = lazy(() => import('./pages/nearMiss/NearMissPage').then((m) => ({ default: m.NearMissPage })));
const WatchtowerPage = lazy(() => import('./pages/watchtower/WatchtowerPage').then((m) => ({ default: m.WatchtowerPage })));
const PremisesPage = lazy(() => import('./pages/premises/PremisesPage').then((m) => ({ default: m.PremisesPage })));
const FridgeTempPage = lazy(() => import('./pages/fridgeTemp/FridgeTempPage').then((m) => ({ default: m.FridgeTempPage })));
const PharmacyStockPage = lazy(() => import('./pages/pharmacyStock/PharmacyStockPage').then((m) => ({ default: m.PharmacyStockPage })));
const CdRegisterPage = lazy(() => import('./pages/cdRegister/CdRegisterPage').then((m) => ({ default: m.CdRegisterPage })));
const NmsPage = lazy(() => import('./pages/nms/NmsPage').then((m) => ({ default: m.NmsPage })));
const PharmacyHubPage = lazy(() => import('./pages/pharmacyHub/PharmacyHubPage').then((m) => ({ default: m.PharmacyHubPage })));
const CarersPage = lazy(() => import('./pages/carers/CarersPage').then((m) => ({ default: m.CarersPage })));
const DailyTakingsPage = lazy(() => import('./pages/dailyTakings/DailyTakingsPage').then((m) => ({ default: m.DailyTakingsPage })));
const DaaPage = lazy(() => import('./pages/daa/DaaPage').then((m) => ({ default: m.DaaPage })));
const PcnPage = lazy(() => import('./pages/pcn/PcnPage').then((m) => ({ default: m.PcnPage })));
const PipsPage = lazy(() => import('./pages/pips/PipsPage').then((m) => ({ default: m.PipsPage })));
const PmrImportPage = lazy(() => import('./pages/pmrImport/PmrImportPage').then((m) => ({ default: m.PmrImportPage })));
const ServiceOpportunitiesPage = lazy(() => import('./pages/serviceOpportunities/ServiceOpportunitiesPage').then((m) => ({ default: m.ServiceOpportunitiesPage })));
const RpLogPage = lazy(() => import('./pages/rpLog/RpLogPage').then((m) => ({ default: m.RpLogPage })));
const PharmacistAuthorisationsPage = lazy(() => import('./pages/pharmacistAuthorisations/PharmacistAuthorisationsPage').then((m) => ({ default: m.PharmacistAuthorisationsPage })));
const SupervisedPage = lazy(() => import('./pages/supervised/SupervisedPage').then((m) => ({ default: m.SupervisedPage })));
const PharmacyRotaPage = lazy(() => import('./pages/pharmacyRota/PharmacyRotaPage').then((m) => ({ default: m.PharmacyRotaPage })));
const DispensaryActivityPage = lazy(() => import('./pages/dispensaryActivity/DispensaryActivityPage').then((m) => ({ default: m.DispensaryActivityPage })));
const PharmacyFirstShell = lazy(() => import('./pages/pharmacyFirst/PharmacyFirstShell').then((m) => ({ default: m.PharmacyFirstShell })));
const VaccinationsPage = lazy(() => import('./pages/vaccinations/VaccinationsPage').then((m) => ({ default: m.VaccinationsPage })));
const ContraceptionShell = lazy(() => import('./pages/contraception/ContraceptionShell').then((m) => ({ default: m.ContraceptionShell })));
const PharmacyDataPage = lazy(() => import('./pages/pharmacyData/PharmacyDataPage').then((m) => ({ default: m.PharmacyDataPage })));
const DrugTariffPage = lazy(() => import('./pages/drugTariff/DrugTariffPage').then((m) => ({ default: m.DrugTariffPage })));
const LocumLedgerPage = lazy(() => import('./pages/locumLedger/LocumLedgerPage').then((m) => ({ default: m.LocumLedgerPage })));
const LocumPortalPage = lazy(() => import('./pages/locumPortal/LocumPortalPage').then((m) => ({ default: m.LocumPortalPage })));
const LocumAppPage = lazy(() => import('./pages/locumApp/LocumAppPage').then((m) => ({ default: m.LocumAppPage })));
/* The Employee app itself (/employee). Imported under another name because
   main's Administration settings page for it is also called EmployeeAppPage
   (pages/admin, at /admin/employee-app) — two imports of one name do not
   compile, and git merges them without a conflict. */
const EmployeeApp = lazy(() => import('./pages/employeeApp/EmployeeAppPage').then((m) => ({ default: m.EmployeeAppPage })));
const CareHomeContractsPage = lazy(() => import('./pages/careHomeContracts/CareHomeContractsPage').then((m) => ({ default: m.CareHomeContractsPage })));
const VaultPage = lazy(() => import('./pages/vault/VaultPage').then((m) => ({ default: m.VaultPage })));
const IgGovernancePage = lazy(() => import('./pages/igGovernance/IgGovernancePage').then((m) => ({ default: m.IgGovernancePage })));
const InsightsPage = lazy(() => import('./pages/insights/InsightsPage').then((m) => ({ default: m.InsightsPage })));
const WallboardAdminPage = lazy(() => import('./pages/wallboard/WallboardAdminPage').then((m) => ({ default: m.WallboardAdminPage })));
const WallboardDisplayPage = lazy(() => import('./pages/wallboard/WallboardDisplayPage').then((m) => ({ default: m.WallboardDisplayPage })));
const RecruitmentPage = lazy(() => import('./pages/recruitment/RecruitmentPage').then((m) => ({ default: m.RecruitmentPage })));
const SageImportPage = lazy(() => import('./pages/sageImport/SageImportPage').then((m) => ({ default: m.SageImportPage })));
const HandoverPage = lazy(() => import('./pages/handover/HandoverPage').then((m) => ({ default: m.HandoverPage })));
const QueryLogPage = lazy(() => import('./pages/queryLog/QueryLogPage').then((m) => ({ default: m.QueryLogPage })));
const IgWorkbookPage = lazy(() => import('./pages/igWorkbook/IgWorkbookPage').then((m) => ({ default: m.IgWorkbookPage })));
const PmrPage = lazy(() => import('./pages/pmr/PmrPage').then((m) => ({ default: m.PmrPage })));
const Fp34cPage = lazy(() => import('./pages/fp34c/Fp34cPage').then((m) => ({ default: m.Fp34cPage })));
const EmsPage = lazy(() => import('./pages/ems/EmsPage').then((m) => ({ default: m.EmsPage })));
const SmokingCessationPage = lazy(() => import('./pages/smokingCessation/SmokingCessationPage').then((m) => ({ default: m.SmokingCessationPage })));
const HypertensionPage = lazy(() => import('./pages/hypertension/HypertensionPage').then((m) => ({ default: m.HypertensionPage })));
const YellowCardPage = lazy(() => import('./pages/yellowCard/YellowCardPage').then((m) => ({ default: m.YellowCardPage })));
const ScriptWatchPage = lazy(() => import('./pages/scriptWatch/ScriptWatchPage').then((m) => ({ default: m.ScriptWatchPage })));
const AnticoagPage = lazy(() => import('./pages/anticoag/AnticoagPage').then((m) => ({ default: m.AnticoagPage })));
const WelshDmrPage = lazy(() => import('./pages/welshDmr/WelshDmrPage').then((m) => ({ default: m.WelshDmrPage })));
const CasPage = lazy(() => import('./pages/cas/CasPage').then((m) => ({ default: m.CasPage })));
const BlisterSpecialsPage = lazy(() => import('./pages/blisterSpecials/BlisterSpecialsPage').then((m) => ({ default: m.BlisterSpecialsPage })));
const CareHomeAuditsPage = lazy(() => import('./pages/careHomeAudits/CareHomeAuditsPage').then((m) => ({ default: m.CareHomeAuditsPage })));
const ReceivePaperworkPage = lazy(() => import('./pages/careHomeCycles/ReceivePaperworkPage').then((m) => ({ default: m.ReceivePaperworkPage })));
const GphcSmartcardsPage = lazy(() => import('./pages/gphcSmartcards/GphcSmartcardsPage').then((m) => ({ default: m.GphcSmartcardsPage })));
const StubPage = lazy(() => import('./pages/StubPage').then((m) => ({ default: m.StubPage })));
import './styles/theme.css';
const AssistPage = lazy(() => import('./pages/assist/AssistPage').then((m) => ({ default: m.AssistPage })));
const AssistQueuePage = lazy(() => import('./pages/platformAdmin/AssistQueuePage').then((m) => ({ default: m.AssistQueuePage })));

const RotaPage = lazy(() => import('./pages/rota/RotaPage').then((m) => ({ default: m.RotaPage })));
const HealthSafetyPage = lazy(() => import('./pages/hs/HealthSafetyPage').then((m) => ({ default: m.HealthSafetyPage })));
const UsersPermissionsPage = lazy(() => import('./pages/administration/UsersPermissionsPage').then((m) => ({ default: m.UsersPermissionsPage })));
const TrainingPage = lazy(() => import('./pages/training/TrainingPage').then((m) => ({ default: m.TrainingPage })));
const ComplianceGatePage = lazy(() => import('./pages/gate/ComplianceGatePage').then((m) => ({ default: m.ComplianceGatePage })));
const ApprovedSuppliersPage = lazy(() => import('./pages/approvedSuppliers/ApprovedSuppliersPage').then((m) => ({ default: m.ApprovedSuppliersPage })));
const CustomerCrmPage = lazy(() => import('./pages/customerCrm/CustomerCrmPage').then((m) => ({ default: m.CustomerCrmPage })));
const FrontDeskPage = lazy(() => import('./pages/frontDesk/FrontDeskPage').then((m) => ({ default: m.FrontDeskPage })));
const SalesPage = lazy(() => import('./pages/sales/SalesPage').then((m) => ({ default: m.SalesPage })));
const MarketingPage = lazy(() => import('./pages/marketing/MarketingPage').then((m) => ({ default: m.MarketingPage })));

/*
 * 2026-08-26 — the single largest thing in the main chunk, moved out of it.
 *
 * The build was 5,228 kB against a 5,243 kB precache cap: fifteen kilobytes
 * of headroom, so the next person to add anything at all broke `vite build`.
 * The cap comment in vite.config.ts has said since it was raised that the
 * proper fix is to code-split; raising it again would have been the same
 * stopgap a second time.
 *
 * Measured rather than guessed. Decoding the chunk's own sourcemap and
 * attributing generated bytes back to their modules put Enquiries &
 * Quotations at 858 kB — sixteen per cent of the chunk and more than twice
 * the next area. Two other pages were tried lazily first and freed nothing,
 * because something else imported them eagerly and a lazy() only splits when
 * NOTHING reaches the module eagerly. App.tsx is the only importer of this
 * one, which is why it works.
 */
const EnquiriesQuotationsPage = lazy(() =>
  import('./pages/switchboard/EnquiriesQuotationsPage').then((m) => ({
    default: m.EnquiriesQuotationsPage,
  })),
);
const BuildingMaintenancePage = lazy(() => import('./pages/buildingMaintenance/BuildingMaintenancePage'));
const AccreditationsPage = lazy(() => import('./pages/accreditations/AccreditationsPage'));
const FleetPage = lazy(() => import('./pages/fleet/FleetPage'));
const PlantEquipmentPage = lazy(() => import('./pages/plantEquipment/PlantEquipmentPage'));
// ASSETS-MAINTENANCE-001 — also serves the Vehicles section as ?type=vehicle.
const MaintenanceSchedulePage = lazy(
  () => import('./pages/assetsMaintenance/MaintenanceSchedulePage'),
);
const JobsPage = lazy(() => import('./pages/jobs/JobsPage'));
// SPEC-51 Stage 1 - Standard Sales & Jobs. Its own route, not a query parameter
// on somebody else's: /enquiries-quotations and ?build=switchboard are one route
// with a flag, which is the shape this vertical exists to stop repeating.
const StandardSalesPage = lazy(() => import('./pages/standardSales/StandardSalesPage'));
const ErpAccountsPage = lazy(() => import('./pages/erpAccounts/ErpAccountsPage'));
// The TrueLayer redirect URI (2026-09-30). Lazy like its parent: the entry
// chunk sits just under the workbox precache cap.
const BankFeedCallbackPage = lazy(() => import('./pages/erpAccounts/BankFeedCallbackPage'));
// The HMRC Making Tax Digital redirect URI (2026-10-28). Lazy for the same reason.
const HmrcVatCallbackPage = lazy(() => import('./pages/erpAccounts/HmrcVatCallbackPage'));
const AuditPackPage = lazy(() => import('./pages/auditPack/AuditPackPage').then((m) => ({ default: m.AuditPackPage })));
/*
 * Care-home cycle work and dossette billing — lazy, because importing them
 * statically pushed the entry chunk past the 5 MiB workbox precache cap and
 * failed `vite build`. That failure is production-only: type-check and unit
 * tests cannot see it, which is why pre-flight catches it and nothing else does.
 */
const ServiceFeesPage = lazy(() => import('./pages/serviceFees/ServiceFeesPage').then((m) => ({ default: m.ServiceFeesPage })));
const QualificationsMatrixPage = lazy(() => import('./pages/hr/QualificationsMatrixPage').then((m) => ({ default: m.QualificationsMatrixPage })));
const CyclePage = lazy(() => import('./pages/careHomeCycles/CyclePage').then((m) => ({ default: m.CyclePage })));
const ResidentPage = lazy(() => import('./pages/careHomeCycles/ResidentPage').then((m) => ({ default: m.ResidentPage })));
const StartCyclePage = lazy(() => import('./pages/careHomeCycles/StartCyclePage').then((m) => ({ default: m.StartCyclePage })));
const DossetteBillingPage = lazy(() => import('./pages/dossetteBilling/DossetteBillingPage').then((m) => ({ default: m.DossetteBillingPage })));
const DeliveryWallChartPage = lazy(() => import('./pages/careHomes/DeliveryWallChartPage').then((m) => ({ default: m.DeliveryWallChartPage })));
const CareHomeCyclesPage = lazy(() => import('./pages/careHomeCycles/CareHomeCyclesPage').then((m) => ({ default: m.CareHomeCyclesPage })));
const CareHomesPage = lazy(() => import('./pages/careHomes/CareHomesPage').then((m) => ({ default: m.CareHomesPage })));

/*
 * 2026-08-26 — Stock Management is LAZY. It was a static import, so the whole
 * section sat in the main bundle: 18,331 lines across pages/stock, of which
 * ProStockView.tsx alone is 11,824.
 *
 * WHY NOW. The production build failed — the main chunk reached 5,258,670 bytes
 * against the 5 MiB precache cap in vite.config.ts. main alone builds at
 * 5,164,450, i.e. 78 KB under it, so the stock work tipped it by 16 KB. The
 * comment on that cap has been saying the real answer for a while: "The proper
 * fix is to code-split that chunk".
 *
 * Raising the cap a third time was the alternative and it is the wrong one:
 * each raise makes the eventual split bigger, and a 5 MB precache is not a
 * working PWA on a phone whatever the config says.
 *
 * This section, not somebody else's. App.tsx has 112 static page imports
 * against 9 lazy, and several are larger still (PlatformAdminTenantsPage is
 * 6,897 lines) — but stock is the biggest single tree, it is the one this
 * branch grew, and converting the rest is a platform job rather than a thing
 * to do quietly from a section branch.
 *
 * The named export is mapped to `default` because React.lazy expects a default
 * export and this page has never had one.
 */
const StockManagementPage = lazy(() =>
  import('./pages/stock/StockManagementPage').then((m) => ({
    default: m.StockManagementPage,
  })),
);

/**
 * Route guard — redirects unauthenticated users to /login, preserving the
 * path they were trying to reach so LoginPage can bounce them back after a
 * successful sign-in.
 *
 * No hooks beyond useLocation. `getAccessToken()` reads localStorage on
 * every render, so when LoginPage sets the token and navigates, React
 * Router re-renders this guard, which sees the new token and lets the
 * user through. No effects, no state, no races.
 */
function RequireAuth({ children }: { children: JSX.Element }): JSX.Element {
  const location = useLocation();
  if (!getAccessToken()) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  // Nation split (2026-08-24) — the branch selector lives inside the auth gate:
  // /api/branches is authenticated, so mounting the provider any higher would
  // fire an unauthenticated request on the login screen.
  return <BranchProvider><AgreementGate>{children}</AgreementGate></BranchProvider>;
}

/**
 * Nothing but the agreement until the company has agreed.
 *
 * NOT THE GATE. verifyTenant refuses every other route with
 * AGREEMENT_REQUIRED; this stops a director staring at a screen full of
 * failed requests and shows them the one thing they can do. So it fails OPEN
 * on error: the server is already saying no, and a client that guessed wrong
 * would lock somebody out of a platform they had agreed to.
 */
function AgreementGate({ children }: { children: JSX.Element }): JSX.Element {
  const q = useQuery({
    queryKey: ['tenant', 'agreement'],
    queryFn: () => fetchAgreementToSign(),
    retry: false,
    staleTime: 60_000,
  });
  // Within the days to sign, a director may carry on for this visit; after them, not.
  const [later, setLater] = useState(false);
  if (q.isPending || q.isError) return children;
  if (!q.data.required || (later && !q.data.mustSignNow)) return children;
  return <AgreementPage onContinue={q.data.mustSignNow ? undefined : () => setLater(true)} />;
}

/**
 * Root-path router (2026-06-04).
 *
 * The PWA manifest's `start_url` is `/`, so when a user taps an
 * installed home-screen icon Chrome opens `/` rather than whatever
 * URL the install was captured against. For tenant users that's
 * fine — `/` falls through to RequireAuth → /login → /guidance.
 *
 * But provisioned **devices** (driver tablets, wall kiosks) don't
 * have a tenant JWT — they authenticate via their own localStorage
 * device token. Without this redirect, tapping the icon lands them
 * on /login (or /kiosk's "paste device token" page) even though
 * they're already paired.
 *
 * Precedence: a REAL user session first, then driver-tablet token
 * (most cab-bound), then kiosk token (wall-mount). A visitor with
 * none of them gets the standard tenant flow.
 *
 * 2026-09-03 — the device checks used to run BEFORE the session
 * check, and that was wrong in a way nobody would guess from this
 * file. Admin > Open Kiosk mints a fresh kiosk token into
 * localStorage on every open (KioskSetupPage.tsx), and neither
 * "Close kiosk" nor logout() clears it — so a director's everyday
 * browser carries one for ever. Any signed-in user of such a
 * browser who reached "/" was thrown to the clock-in screen.
 *
 * The case that proved it: ImpersonationLandingPage sets the access
 * token and then navigates to "/". With the device check first, the
 * token it had just minted was ignored and the platform admin landed
 * on a kiosk instead of the tenant they asked to sign in as.
 *
 * A signed-in session is the stronger claim: a paired device has no
 * user token, so the device redirects still fire for the case they
 * exist for — an icon tap at "/" with nobody signed in. TODO 078.
 */
function RootRedirect(): JSX.Element {
  if (!getAccessToken()) {
    if (getDriverTabletToken()) {
      return <Navigate to="/driver-tablet" replace />;
    }
    if (getKioskToken()) {
      return <Navigate to="/kiosk" replace />;
    }
    // No identity at all — show the public platform landing page.
    return <Navigate to="/platform" replace />;
  }
  // Signed in — defer to the standard auth flow.
  return (
    <RequireAuth>
      <DefaultLanding />
    </RequireAuth>
  );
}

/**
 * Default post-auth landing, for somebody who reaches `/` while already
 * signed in — clicking the logo, or a bookmark of the bare origin.
 *
 * THE RULE LIVES IN lib/landingRoute.ts NOW. It was written here and twice
 * more in LoginPage, and only this copy knew about the Transport Manager
 * carve-out — so the rule was right in one of the three places it existed.
 * A normal sign-in never reaches this function.
 */
function DefaultLanding(): JSX.Element {
  return <Navigate to={landingRoute()} replace />;
}

/**
 * Route tree — exported so tests can mount it inside a `MemoryRouter`
 * without the `<BrowserRouter>` that `<App />` provides.
 */
export function AppRoutes(): JSX.Element {
  // Every page is lazy, so its code downloads when it is opened; this boundary covers the pages outside the shell.
  return (
    <Suspense fallback={null}>
    <Routes>
      {/*
        2026-06-04 — Device-aware root. Driver tablets and wall
        kiosks land here from PWA start_url='/'; we route them to
        their kind-specific surface so the icon "just opens the
        app". See RootRedirect above.
      */}
      <Route path="/" element={<RootRedirect />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/platform" element={<PlatformPage />} />
      {/* Public onboarding registration — no AppShell, no login.
          The :token IS the auth artefact. New starter sets password
          + PIN here, then logs in via /login. */}
      <Route path="/onboarding/:token" element={<OnboardingRegisterPage />} />
      {/* spec-54: the public sign-up and its confirm link. No sign-in. */}
      <Route path="/signup" element={<SignupPage />} />
      {/* Public: read before you agree, so no session and no tenant. */}
      <Route path="/terms" element={<LegalDocumentPage kind="terms" title="Terms of service" />} />
      <Route path="/privacy" element={<LegalDocumentPage kind="privacy" title="Privacy notice" />} />
      <Route path="/signup/confirm" element={<SignupConfirmPage />} />
      {/* Public signing page — no auth, no AppShell. Counterparties don't have JeanieIQ accounts. */}
      <Route path="/sign/:token" element={<ContractsSignPage />} />
      {/* Public display wallboard — no auth, no AppShell. The :token is the screen's access artefact. */}
      <Route path="/display/:token" element={<WallboardDisplayPage />} />
      {/*
        Kiosk page — public route, no AppShell, no user JWT. Tablet
        authenticates with a per-device token issued by a director.
        See apps/web/src/pages/kiosk/KioskPage.tsx for the three-state
        flow (setup / boot / confirm). Phase C.1.
      */}
      <Route path="/kiosk" element={<KioskPage />} />
      {/*
        Kiosk provisioning — Stage 1.2d (2026-05-10). Public route the
        QR code lands on. Consumes the one-shot provisioning token,
        stores the device token in localStorage, then redirects to
        /kiosk. The :token is scrubbed from the URL on mount.
      */}
      <Route path="/kiosk/provision/:token" element={<KioskProvisionPage />} />
      <Route path="/kiosk/provision" element={<KioskProvisionPage />} />
      {/*
        Driver Tablet — Phase T0 (2026-06-03). Public route mirroring
        /kiosk: no AppShell, no user JWT (T0 anonymous preview; T1 adds
        biometric/PIN via the existing kiosk infrastructure). Same
        component renders inside Kiosk Setup → Fleet Kiosk tab so a
        director can review the design from desktop.
        See apps/web/src/pages/driverTablet/DriverTabletPage.tsx.
      */}
      <Route path="/driver-tablet" element={<DriverTabletPage />} />
      {/*
        THE LOCUM APP (2026-08-26) — public route, no AppShell, its own auth
        realm. Placed here with /kiosk and /driver-tablet deliberately.

        /locum-portal renders INSIDE the tenant AppShell (it sits far below,
        past RequireAuth), so a locum who installed it to their home screen got
        the full staff sidebar and desktop chrome on a 390px phone. This is the
        surface that should have had this treatment all along.

        A locum is not a tenant user: they work for several pharmacy groups at
        once and sign in against /api/locum-auth with a token that carries no
        tenantId. /locum-portal is NOT replaced — it stays put and becomes
        explicitly what it already half was, the admin preview where a director
        opens a locum's portal via ?locumName=.

        See apps/web/src/pages/locumApp/LocumAppPage.tsx.
      */}
      <Route path="/locum" element={<LocumAppPage />} />
      {/* The Employee app (2026-09-24) — every tenant's own staff, on their own
          phones. Above the AppShell like /locum, so an installed app shows no
          staff sidebar. Unlike /locum it signs in as a NORMAL platform user
          (role employee) and reads /api/me/*. See pages/employeeApp. */}
      <Route path="/employee" element={<EmployeeApp />} />
      {/* Auditor portal — public route, own scoped session token, read-only
          financial-only access. /audit-portal/redeem consumes the invite. */}
      <Route path="/audit-portal" element={<AuditorPortalPage />} />
      <Route path="/audit-portal/redeem" element={<AuditorPortalPage />} />
      {/* Care home portal — public route, own scoped session token. The person
          here is the pharmacy's CUSTOMER, not its staff: no app shell, no user
          JWT, and a grant that reaches one home. /redeem consumes the invite. */}
      <Route path="/care-home-portal" element={<CareHomePortalPage />} />
      <Route path="/care-home-portal/redeem" element={<CareHomePortalPage />} />
      {/* PH-CARE-16: a care home signs its own contract - public, like the portal. */}
      <Route path="/contract-sign" element={<ContractSignPage />} />
      <Route path="/satisfaction" element={<SatisfactionSurveyPage />} />
      <Route path="/supplier-questionnaire" element={<SupplierQuestionnairePage />} />
      {/* Read and sign (2026-09-26): staff sign a memo or SOP by the link they were emailed - public, like the contract page. */}
      <Route path="/read-sign" element={<ReadSignPage />} />
      {/* The external ISO auditor opens one standard's evidence by the link from Auditor access - public. */}
      <Route path="/audit" element={<AuditAccessPage />} />
      {/* PH-CARE-07: where GoCardless returns a payer who has just set up their
          Direct Debit. Public, because they are on their own phone with no
          login here; the one-use token in the link finishes that one mandate. */}
      <Route path="/dd/return" element={<DossetteReturnPage />} />
      {/* Task 12 — JeanieIQ's own subscription billing, the emailed-link
          flow: /dd/subscription?k= is where the link opens, /dd/subscription/
          return?k=&m= is where GoCardless returns the payer. Public, same
          reasoning as /dd/return above: the token is the only credential. */}
      <Route path="/dd/subscription" element={<DirectDebitLinkPage />} />
      <Route path="/dd/subscription/return" element={<DirectDebitLinkReturnPage />} />
      {/*
        Driver Tablet T1.2 (2026-06-03) — public route the QR lands on.
        Consumes the one-shot provisioning token, stores the device
        token under the driver-tablet-specific localStorage key, then
        redirects to /driver-tablet. Mirror of /kiosk/provision but
        keeps the two kinds' tokens scoped to their own surface.
      */}
      <Route
        path="/driver-tablet/provision/:token"
        element={<DriverTabletProvisionPage />}
      />
      <Route
        path="/driver-tablet/provision"
        element={<DriverTabletProvisionPage />}
      />
      {/*
        JN-ADMIN-001 (2026-05-27) — Platform admin. SEPARATE auth from
        the tenant /login route. /admin/login is the entry; once
        signed in the admin lands on /admin (Home — the menu landing).
        The pages render
        outside the tenant AppShell on purpose — different surface,
        different visual treatment so it can never be mistaken for a
        customer-facing screen.
      */}
      <Route path="/admin/login" element={<PlatformAdminLoginPage />} />
      <Route path="/admin/forgot-password" element={<AdminForgotPasswordPage />} />
      <Route path="/admin/reset-password" element={<AdminResetPasswordPage />} />
      <Route path="/admin/tenants" element={<AdminSectionGate section="tenants"><PlatformAdminTenantsPage /></AdminSectionGate>} />
      {/* JN-ADMIN-DETAIL-001 — per-tenant detail page reachable by
          clicking a row on /admin/tenants. */}
      <Route path="/admin/tenants/:id" element={<AdminSectionGate section="tenants"><PlatformAdminTenantDetailPage /></AdminSectionGate>} />
      {/* JN-PLATFORM-SETTINGS-001 — operator settings page. */}
      <Route path="/admin/settings" element={<AdminSectionGate><PlatformAdminSettingsPage /></AdminSectionGate>} />
      {/* 2026-06-28 — Country Configuration (Shane + Matt). Stage 1 skeleton. */}
      <Route path="/admin/country-config" element={<AdminSectionGate section="country-config"><CountryConfigPage /></AdminSectionGate>} />
      {/* 2026-10-03 — the country this admin is working in (TODO 528). */}
      <Route path="/admin/country" element={<AdminSectionGate><WorkingCountryPage /></AdminSectionGate>} />
      {/* 2026-06-28 — Failed Logins moved off the Tenants page (Shane + Matt). */}
      <Route path="/admin/failed-logins" element={<AdminSectionGate section="failed-logins"><FailedLoginsPage /></AdminSectionGate>} />
      {/* 2026-10-02 — the Error Log: faults on every tenant (errorLogService). */}
      <Route path="/admin/error-log" element={<AdminSectionGate section="error-log"><ErrorLogPage /></AdminSectionGate>} />
      {/* JN-EARLY-ACCESS-001 (2026-09-19) — early-access sign-ups: list, notify
          address, and per-lead onboarding-date send. */}
      <Route path="/admin/early-access" element={<AdminSectionGate section="early-access"><EarlyAccessPage /></AdminSectionGate>} />
      {/* spec-54 area E: the public sign-ups, and the ones waiting for a person. */}
      <Route path="/admin/signup-requests" element={<AdminSectionGate section="signup-requests"><SignupRequestsPage /></AdminSectionGate>} />
      <Route path="/admin/locums" element={<AdminSectionGate section="locums"><LocumsPage /></AdminSectionGate>} />
      {/* Task 12 — Platform Admin worklist for JeanieIQ's own subscription
          billing (what needs a person; worklistService.ts). */}
      <Route path="/admin/subscription-billing" element={<AdminSectionGate section="subscription-billing"><SubscriptionBillingPage /></AdminSectionGate>} />
      {/* The price list: bands, add-ons, show codes (2026-09-24). */}
      <Route path="/admin/pricing" element={<AdminSectionGate section="pricing"><PlatformAdminPricingPage /></AdminSectionGate>} />
      {/* 2026-06-28 — Admins + Audit Log moved off the Tenants page (Shane + Matt). */}
      <Route path="/admin/admins" element={<AdminSectionGate section="admins"><AdminsPage /></AdminSectionGate>} />
      <Route path="/admin/audit-log" element={<AdminSectionGate section="audit-log"><AuditLogPage /></AdminSectionGate>} />
      {/*
        * JeanieIQ's own governance documents — platform admin only. Reached from
        * the Home quick actions; Shane asked for the top bar and noted *"it might
        * move later"*, and nothing here makes moving it hard.
        */}
      {/* The GDPR Vault is an area of Country Configuration (2026-10-03); the old
          address still lands on it. */}
      <Route path="/admin/gdpr-vault" element={<Navigate to="/admin/country-config?area=gdpr_vault" replace />} />
      {/*
        * The Assist queue — tenants who have asked US (2026-09-24). Reached from
        * the count tile on the admin home. The per-tenant "ask for access" button
        * on the tenant detail page is the other direction and stays where it is.
        */}
      <Route path="/admin/assist-requests" element={<AdminSectionGate section="assist-requests"><AssistQueuePage /></AdminSectionGate>} />
      {/* JN-ADMIN-IMP-001 — public landing for the impersonation token
          (consumes ?token=, stashes it, redirects to /). Auth-gating
          happens via the token itself: malformed/missing => error
          screen, valid => normal app guard takes over after redirect. */}
      <Route path="/admin/impersonate" element={<ImpersonationLandingPage />} />
      {/* JN-ADMIN-HOME-001 — /admin is now a real landing page with
          alert tiles, totals and recent activity. /admin/tenants
          remains the canonical list view. */}
      <Route path="/admin" element={<AdminSectionGate><PlatformAdminHomePage /></AdminSectionGate>} />
      {/*
        HR-FR-001 (2026-05-26) — Fire Register PRINT view. Auth-gated
        but renders OUTSIDE the AppShell so the printed page has no
        sidebar / header chrome. Shown in the document viewer by the
        FireRegisterPanel's "Print LIVE" button, which prints it.
      */}
      <Route
        path="/hr/fire-register/print"
        element={
          <RequireAuth>
            <FireRegisterPrintView />
          </RequireAuth>
        }
      />
      {/* 2026-06-04 — Tachograph file review PRINT view (the
          client-side CSS-print page that lived at
          /fleet/tachograph/print/:fileId) was retired in favour of
          the server-side pdfkit PDF at
          GET /api/fleet/tachograph/files/:id/print.pdf — Chromium's
          save-as-PDF flow collapsed the long content to a single
          page regardless of CSS break hints. Print button on
          TachoReviewModal now opens the server PDF via
          apiOpenInNewTab() (commits 4f57fa11 + b37b540b +
          a0d39e69 + 9bbfee64). */}
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<DefaultLanding />} />
        <Route path="/guidance" element={<GuidancePage />} />
        {/*
          BESIDE Guidance, not under /admin. Every /admin route is gated to
          director, manager and admin — the one place a library every user
          needs cannot live. This sits behind RequireAuth and nothing else.
        */}
        <Route path="/how-to" element={<HowToPage />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        {/* The backup kiosk, opened from the dashboard without going into
            Administration (2026-10-15). The kiosk route on the server is the
            gate: director, manager and admin. */}
        <Route path="/backup-kiosk" element={<BackupKioskPage />} />
        <Route path="/hr" element={<HrPage />} />
        <Route
          path="/hr/qualifications-matrix"
          element={
            <Suspense fallback={null}>
              <QualificationsMatrixPage />
            </Suspense>
          }
        />
        <Route path="/my-profile" element={<MyProfilePage />} />
        <Route path="/payroll" element={<PayrollPage />} />
        {/* Lazy, so it needs its own Suspense boundary like the other lazy
            routes — without one, navigating here suspends synchronously and
            throws (see the AuditPackPage note below). fallback={null} matches
            the existing lazy routes: the sidebar and header are already on
            screen, so a spinner in the content area would flash for one frame
            and read as a fault. */}
        <Route
          path="/stock"
          element={
            <Suspense fallback={null}>
              <StockManagementPage />
            </Suspense>
          }
        />
        <Route path="/motor-trade" element={<MotorTradePage />} />
        <Route path="/directory" element={<DirectoryPage />} />
        {/* Admin — kiosk setup (Sites + Kiosk Devices) */}
        <Route path="/admin/kiosk-setup" element={<KioskSetupPage />} />
        {/* 2026-06-05 (FT-DTAB-A14 phase 2) — Weekly Rest Compensation TM admin. */}
        <Route path="/admin/weekly-rest-compensation" element={<WeeklyRestCompensationPage />} />
        {/* 2026-06-05 — Walkaround Checks (DVSA evidence) TM admin. */}
        <Route path="/admin/walkaround-checks" element={<WalkaroundChecksPage />} />
        <Route path="/admin/roles" element={<RolesPage />} />
        <Route path="/admin/departments" element={<DepartmentsPage />} />
        <Route path="/admin/org-chart" element={<OrgChartPage />} />
        <Route path="/admin/users" element={<Suspense fallback={null}><UsersPermissionsPage /></Suspense>} />
        <Route path="/admin/setup-journey" element={<SetupJourneyPage />} />
        <Route path="/admin/employee-app" element={<EmployeeAppPage />} />
        {/* JN-DIR-SUBS-001 — subscription + features. DIRECTORS ONLY, and the
            PAGE enforces it now: this comment used to say "sidebar gates the
            role", which meant anyone who typed the URL walked straight in. */}
        <Route path="/admin/subscription" element={<SubscriptionPage />} />
        {/* Task 12 — where GoCardless returns the director after the
            button-driven Direct Debit set-up on this page. */}
        <Route path="/admin/subscription/direct-debit/return" element={<DirectDebitReturnPage />} />
        {/* 2026-06-03 — Administration hub. Tenant-level director
            landing that gathers Subscription, Sites, Vehicles,
            Kiosks, Users, Compliance Report and Document Register
            into one card grid (per Shane: "scattered" → consolidate).
            Director-only check in the page. */}
        <Route path="/administration" element={<AdministrationPage />} />
        <Route path="/admin/pay-rate-structures" element={<PayRateStructuresAdminPage />} />
        <Route path="/admin/time-zone" element={<TimeZoneAdminPage />} />
        <Route path="/admin/invoice-branding" element={<InvoiceBrandingPage />} />
        {/* Phase 1.4b (2026-05-20) — Document Register (ISO 9001 §7.5
            controlled-document store). Detail route uses :id; both
            pages live in apps/web/src/pages/admin/. */}
        <Route path="/admin/documents" element={<DocumentRegisterPage />} />
        <Route path="/admin/documents/:id" element={<DocumentDetailPage />} />
        {/*
          Spec-34 §7 originally specified /base-directory as the
          canonical directory route. Platform sidebar + existing deep-
          links use /directory. Brand-rename Phase 1.1 redirects the
          legacy path to the canonical /directory so old bookmarks /
          links keep working but no longer leak the old brand name.
        */}
        <Route path="/base-directory" element={<Navigate to="/directory" replace />} />
        <Route path="/jeanieiq-directory" element={<Navigate to="/directory" replace />} />
        <Route path="/glossary" element={<GlossaryPage />} />
        {/*
          * The company calendar. NOT section-gated: it spans sections and
          * filters to what the caller holds on the server, so gating the route
          * on one section would shut out everybody who holds a different one.
          */}
        <Route path="/calendar" element={<CompanyCalendarPage />} />
        {/*
          * JeanieIQ Assist — the tenant's own record of who has had access.
          * Not section-gated: control over who reads your employee records is
          * not something a tenant buys. See routes/assist for the same reasoning
          * on the API side.
          */}
        <Route path="/assist" element={<AssistPage />} />
        <Route path="/training" element={<Suspense fallback={null}><TrainingPage /></Suspense>} />
        <Route path="/contracts" element={<ContractsPage />} />
        <Route path="/information-security" element={<InformationSecurityPage />} />
        <Route path="/information-security/incidents/:id" element={<InformationSecurityIncidentPage />} />
        <Route path="/information-security/access-reviews/:id" element={<AccessReviewPage />} />
        <Route path="/gate" element={<Suspense fallback={null}><ComplianceGatePage /></Suspense>} />
        <Route path="/rota" element={<Suspense fallback={null}><RotaPage /></Suspense>} />
        <Route path="/hs" element={<Suspense fallback={null}><HealthSafetyPage /></Suspense>} />
        <Route path="/customer-crm" element={<Suspense fallback={null}><CustomerCrmPage /></Suspense>} />
        <Route path="/front-desk" element={<Suspense fallback={null}><FrontDeskPage /></Suspense>} />
        <Route path="/sales" element={<Suspense fallback={null}><SalesPage /></Suspense>} />
        <Route path="/marketing" element={<Suspense fallback={null}><MarketingPage /></Suspense>} />
        <Route path="/approved-suppliers" element={<Suspense fallback={null}><ApprovedSuppliersPage /></Suspense>} />
        {/* 2026-05-13 — Finance group add-on modules. Routes always exist;
            sidebar gates the entry on tenant.<flag>Enabled. */}
        <Route path="/sub-contract" element={<SubContractPage />} />
        <Route path="/cashflow" element={<CashflowPage />} />
        {/* Tronc (2026-08-16). Route always exists, sidebar gates on
            tenant.troncEnabled, and every tronc endpoint asserts the
            same flag server-side — TRONC-UNGATED-001. */}
        <Route path="/tronc" element={<TroncPage />} />
        {/* AuditPackPage is lazy-loaded (heavy: FRS102/iXBRL/CT600/xlsx), so
            it needs its own Suspense boundary like the other lazy routes —
            without one, navigating here suspends synchronously and throws. */}
        <Route
          path="/audit-pack"
          element={
            <Suspense fallback={null}>
              <AuditPackPage />
            </Suspense>
          }
        />
        <Route path="/holiday" element={<HolidayPage />} />
        {/* Appraisals & Discipline — between Holiday and H&S in the
            sidebar (Shane, 2026-08-21). Appraisals moved here out of
            Training Records. */}
        <Route
          path="/appraisals-discipline"
          element={<AppraisalsDisciplinePage />}
        />
        <Route path="/near-miss" element={<NearMissPage />} />
        <Route path="/watchtower" element={<WatchtowerPage />} />
        <Route path="/premises" element={<PremisesPage />} />
        <Route path="/fridge-temps" element={<FridgeTempPage />} />
        <Route path="/pharmacy-stock" element={<PharmacyStockPage />} />
        <Route path="/cd-register" element={<CdRegisterPage />} />
        <Route path="/nms" element={<DevolvedRoute labelKey="nms"><NmsPage /></DevolvedRoute>} />
        <Route path="/pharmacy" element={<PharmacyHubPage />} />
        <Route path="/carers" element={<CarersPage />} />
        <Route path="/daily-takings" element={<DailyTakingsPage />} />
        <Route path="/daa" element={<DaaPage />} />
        <Route path="/pcn" element={<PcnPage />} />
        <Route path="/pips" element={<DevolvedRoute labelKey="pips"><PipsPage /></DevolvedRoute>} />
        {/* Both halves are one screen now; the old address lands on its tab. */}
        <Route path="/pf-triage" element={<DevolvedRoute labelKey="pf_triage"><PharmacyFirstShell /></DevolvedRoute>} />
        {/* All three addresses are the one screen; each lands on its tab.
            /pcs keeps its nation gate, so it still 404s outside England —
            /contraception does not, which is how EHC keeps its reach. */}
        <Route path="/contraception" element={<ContraceptionShell />} />
        <Route path="/pcs" element={<DevolvedRoute labelKey="pcs"><ContraceptionShell /></DevolvedRoute>} />
        {/* Import & Export. /pmr-import still answers on its own, for the
            bookmarks and for the clinical registry that names it. */}
        <Route path="/pharmacy-import-export" element={<PharmacyDataPage />} />
        <Route path="/pmr-import" element={<PmrImportPage />} />
        {/* Retired into /compliance; kept so old links and bookmarks still land. */}
        <Route path="/dspt" element={<Navigate to="/compliance" replace />} />
        <Route path="/service-opportunities" element={<ServiceOpportunitiesPage />} />
        <Route path="/rp-log" element={<RpLogPage />} />
        <Route path="/pharmacist-authorisations" element={<PharmacistAuthorisationsPage />} />
        <Route path="/supervised" element={<SupervisedPage />} />
        <Route path="/pharmacy-rota" element={<PharmacyRotaPage />} />
        <Route path="/dispensary-activity" element={<DispensaryActivityPage />} />
        <Route path="/pharmacy-first" element={<DevolvedRoute labelKey="pharmacy_first"><PharmacyFirstShell /></DevolvedRoute>} />
        <Route path="/vaccinations" element={<VaccinationsPage />} />
        <Route path="/ehc" element={<ContraceptionShell />} />
        <Route path="/drug-tariff" element={<DrugTariffPage />} />
        <Route
          path="/service-fees"
          element={
            <Suspense fallback={null}>
              <ServiceFeesPage />
            </Suspense>
          }
        />
        <Route path="/locum-ledger" element={<LocumLedgerPage />} />
        <Route path="/locum-portal" element={<LocumPortalPage />} />
        {/* SOPs are policies now; kept so old links and bookmarks still land. */}
        <Route path="/sops" element={<Navigate to="/hr?tab=policies" replace />} />
        <Route path="/care-home-contracts" element={<CareHomeContractsPage />} />
        <Route path="/vault" element={<VaultPage />} />
        <Route path="/ig-governance" element={<IgGovernancePage />} />
        <Route path="/insights" element={<InsightsPage />} />
        <Route path="/wallboard-admin" element={<WallboardAdminPage />} />
        <Route path="/recruitment" element={<RecruitmentPage />} />
        {/* Retired 2026-08-31: these rendered the same views the Locum Hub
            already carries as tabs, so they were a second door to one room.
            Kept as redirects so existing links and bookmarks still land. */}
        <Route path="/locum-invoices" element={<Navigate to="/locum-ledger?tab=invoices" replace />} />
        <Route path="/sage-import" element={<SageImportPage />} />
        <Route path="/handover" element={<HandoverPage />} />
        <Route path="/query-log" element={<QueryLogPage />} />
        <Route path="/locum-availability" element={<Navigate to="/locum-ledger?tab=availability" replace />} />
        {/* Merged into /compliance; kept so old links and bookmarks still land. */}
        <Route path="/ig-dsp" element={<Navigate to="/compliance?tab=submissions" replace />} />
        <Route path="/compliance" element={<IgWorkbookPage />} />
        <Route path="/pmr" element={<PmrPage />} />
        <Route path="/fp34c" element={<Fp34cPage />} />
        <Route path="/ems" element={<EmsPage />} />
        <Route path="/smoking-cessation" element={<SmokingCessationPage />} />
        <Route path="/hypertension" element={<DevolvedRoute labelKey="hypertension"><HypertensionPage /></DevolvedRoute>} />
        <Route path="/yellow-card" element={<YellowCardPage />} />
        <Route path="/script-watch" element={<ScriptWatchPage />} />
        <Route path="/anticoag" element={<AnticoagPage />} />
        <Route path="/welsh-dmr" element={<DevolvedRoute labelKey="welsh_dmr"><WelshDmrPage /></DevolvedRoute>} />
        <Route path="/cas" element={<CasPage />} />
        <Route path="/blister-specials" element={<BlisterSpecialsPage />} />
        <Route
          path="/dossette-admin"
          element={
            <Suspense fallback={null}>
              <DossetteBillingPage />
            </Suspense>
          }
        />
        <Route
          path="/delivery-wall-chart"
          element={
            <Suspense fallback={null}>
              <DeliveryWallChartPage />
            </Suspense>
          }
        />
        <Route
          path="/care-homes"
          element={
            <Suspense fallback={null}>
              <CareHomesPage />
            </Suspense>
          }
        />
        <Route path="/care-home-audits" element={<CareHomeAuditsPage />} />
        <Route
          path="/care-home-cycles"
          element={
            <Suspense fallback={null}>
              <CareHomeCyclesPage />
            </Suspense>
          }
        />
        {/* Before the :cycleId route — "start" is a literal path, and a
            parameter declared above it would swallow it. */}
        <Route
          path="/care-home-cycles/start"
          element={
            <Suspense fallback={null}>
              <StartCyclePage />
            </Suspense>
          }
        />
        <Route
          path="/care-home-cycles/:cycleId"
          element={
            <Suspense fallback={null}>
              <CyclePage />
            </Suspense>
          }
        />
        <Route
          path="/care-home-cycles/:cycleId/resident/:residentKey"
          element={
            <Suspense fallback={null}>
              <ResidentPage />
            </Suspense>
          }
        />
        <Route path="/care-home-receive" element={<ReceivePaperworkPage />} />
        <Route path="/gphc-smartcards" element={<GphcSmartcardsPage />} />
        {/* Lazy since 2026-08-26 — so it needs its own Suspense boundary like
            every other lazy route here. Without one, navigating to it suspends
            synchronously and throws. */}
        <Route
          path="/enquiries-quotations"
          element={
            <Suspense fallback={null}>
              <EnquiriesQuotationsPage />
            </Suspense>
          }
        />
        <Route
          path="/building-maintenance"
          element={
            <Suspense fallback={null}>
              <BuildingMaintenancePage />
            </Suspense>
          }
        />
        <Route
          path="/accreditations"
          element={
            <Suspense fallback={null}>
              <AccreditationsPage />
            </Suspense>
          }
        />
        <Route
          path="/fleet"
          element={
            <Suspense fallback={null}>
              <FleetPage />
            </Suspense>
          }
        />
        <Route
          path="/plant"
          element={
            <Suspense fallback={null}>
              <PlantEquipmentPage />
            </Suspense>
          }
        />
        <Route
          path="/maintenance-schedule"
          element={
            <Suspense fallback={null}>
              <MaintenanceSchedulePage />
            </Suspense>
          }
        />
        <Route
          path="/jobs"
          element={
            <Suspense fallback={null}>
              <JobsPage />
            </Suspense>
          }
        />
        <Route
          path="/standard-sales"
          element={
            <Suspense fallback={null}>
              <StandardSalesPage />
            </Suspense>
          }
        />
        <Route
          path="/erp-accounts"
          element={
            <Suspense fallback={null}>
              <ErpAccountsPage />
            </Suspense>
          }
        />
        <Route
          path="/erp-accounts/bank-feeds/callback"
          element={
            <Suspense fallback={null}>
              <BankFeedCallbackPage />
            </Suspense>
          }
        />
        <Route
          path="/erp-accounts/hmrc-vat/callback"
          element={
            <Suspense fallback={null}>
              <HmrcVatCallbackPage />
            </Suspense>
          }
        />
        <Route
          path="/credit-control"
          element={<Navigate to="/erp-accounts?tab=sales#credit-control" replace />}
        />
        {/* Old address of the takeover page; the takeover is now a tab. */}
        <Route
          path="/erp-accounts/data-migration"
          element={<Navigate to="/erp-accounts?tab=period-end#takeover" replace />}
        />
        <Route path="/stub/:slug" element={<StubPage />} />
        <Route path="*" element={<DefaultLanding />} />
      </Route>
    </Routes>
    </Suspense>
  );
}

export function App(): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
      {/*
        * Outside the router on purpose: a refusal has to stay on screen when
        * the failed action navigates, and it belongs to the app rather than to
        * whichever page was open. See lib/mutationErrors.ts - a role-gated
        * button that fails silently is indistinguishable from a broken page.
        */}
      <DocumentViewer />
      <MutationErrorToast />
    </QueryClientProvider>
  );
}
